import { Flex } from '@chakra-ui/react';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { CMS } from '@waitroom/models';
import { cmsPagesQuery } from '@waitroom/react-query';
import { toBoolean } from '@waitroom/utils';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { routes } from '../../../constants/routes';
import { Icon } from '../../core/components/Icon/Icon';
import DropdownMenu from '../../core/components/Menu/Dropdown/DropdownMenu';
import { MenuItem, Render } from '../../core/components/Menu/Menu';
import CmsIcon from '../components/Icon/CmsIcon';

const pageCategoryLinks: Record<string, string> = {
  none: '',
  product: routes.PRODUCT.link,
  resources: routes.RESOURCES.link,
  useCases: routes.USE_CASES.link,
  turnBasedMeetings: routes.TURN_BASED_MEETINGS.link,
};

const getCategoryLink = (category: string | undefined) =>
  (category ? pageCategoryLinks[category] : undefined) || routes.PAGE.link;

const buildPageLink = (page: CMS.Page, isNone = false) => {
  if (page.url?.length) {
    return {
      to: page.url,
      target: '_blank',
      rel: 'noopener noreferrer nofollow',
    } satisfies MenuItem;
  }
  if (isNone) {
    return {
      to: `${routes.PAGE.link}/${page.slug?.current || page._id}`,
    } satisfies MenuItem;
  }
  return {
    to: `${getCategoryLink(page.category)}/${page.slug?.current || page._id}`,
  } satisfies MenuItem;
};

export const defaultPageTypes: CMS.PageType[] = ['general', 'footer'];
export const useCmsPagesMenu = (types?: CMS.PageType[]) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search || window.location.search);
  const isPreview = toBoolean(params.get('preview'));
  return useQuery(cmsPagesQuery({ types, isPreview }));
};

export type UseCmsPagesHeaderMenu = {
  items?: MenuItem[];
} & ReturnType<typeof useCmsPagesMenu>;

export const useCmsPagesTopMenu = (types?: CMS.PageType[]): UseCmsPagesHeaderMenu => {
  const location = useLocation();
  const { t } = useTranslation();
  const params = new URLSearchParams(location.search || window.location.search);
  const isPreview = toBoolean(params.get('preview'));
  const response = useQuery(cmsPagesQuery({ types, isPreview }));
  const grouped = response.data?.grouped;

  const items = useMemo<MenuItem[] | undefined>(() => {
    const headerItems = grouped?.general;
    if (!headerItems) return undefined;
    const noneItems = headerItems.none?.map<MenuItem>((page) => ({
      ...buildPageLink(page, true),
      text: page.menuTitle || page.title,
      order: 5,
    }));
    const keys = Object.keys(headerItems) as CMS.PageCategory[];
    const menuItems = keys
      .map<MenuItem | undefined>((key) => {
        const group = headerItems[key];
        if (!group || !group.length || key === 'none') return undefined;
        const render: Render = ({ isModal }) => (
          <DropdownMenu
            hover
            inline={isModal}
            order={5}
            button={
              <>
                <Flex
                  as="span"
                  align="center"
                  textTransform="capitalize"
                  pl={{ base: 0, xl: 2 }}
                  py={{ base: 0, xl: 3 }}
                >
                  {t([`header.menu.${key}`, key])}
                  <Icon icon={faChevronDown} color="gray.500" size="sm" pl={2} />
                </Flex>
              </>
            }
            items={group.map((item, i = 0) => ({
              ...buildPageLink(item, false),
              size: 'sm',
              children: (
                <Fragment key={i}>
                  {item.menuIcon ? (
                    <CmsIcon
                      icon={{ ...item.menuIcon }}
                      mr={2}
                      size={{ base: '3xs', lg: '2xs' }}
                      fontSize={{ base: 'md', lg: 'lg' }}
                    />
                  ) : null}
                  {item.menuTitle || item.title}
                </Fragment>
              ),
            }))}
          />
        );
        return {
          render,
          renderModal: render,
        };
      })
      .filter(Boolean) as MenuItem[];
    return menuItems.concat(noneItems); // add
  }, [grouped, t]);

  return {
    items,
    ...response,
  };
};

export type UseCmsPagesFooterMenu = {
  items?: CMS.Page[] | undefined;
} & ReturnType<typeof useCmsPagesMenu>;
export const useCmsPagesFooterMenu = (types?: CMS.PageType[]): UseCmsPagesFooterMenu => {
  const location = useLocation();
  const params = new URLSearchParams(location.search || window.location.search);
  const isPreview = toBoolean(params.get('preview'));
  const response = useQuery(cmsPagesQuery({ types, isPreview }));
  return {
    items: response.data?.grouped.footer,
    ...response,
  };
};
